// 네이버 CAPA «관리» 탭 ④ 알림 대상(P3b) — 목록 · 추가(사람 검색·봇 채널 고르기) · 수정 · 삭제 · 시험 발송.
// 운영(ops)은 읽기 전용 — 쓰기 단추·입력 disabled(감추지 않는다) · 사람 검색·채널 목록은 부르지 않는다(서버가 403).
// 🔴 쓰기는 탭의 lock/unlock/busy 를 같이 쓴다(탭 전체에서 동시 쓰기 1건). 순서 = isAdmin 가드 → lock →
//    try { confirm → mounted 확인 → 요청 → onChanged } finally { unlock }.
// 🔴 오류는 response.data.detail 만 읽는다.
// 🔴 사람 검색은 300ms 쉬고 부른다 · 요청 번호로 늦은 응답을 버린다 · 언마운트 뒤에는 setState 하지 않는다.
// 🔴 이 카드의 h3 는 제목 하나뿐이다(탭 h3 순서 시험·e2e 가 h3 를 센다) — 그 밖 머리는 h4.
import { Fragment, useEffect, useRef, useState } from 'react';
import { capaAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, badge, btn, card, input } from '../../utils/uiStyles';
import {
  DASH, NOTIFY_KINDS, NOTIFY_KIND_LABELS, detailText, errorText, kindsText, lastSendText, sameValue, targetText,
  testResultText,
} from './capaView';

const SEARCH_DELAY_MS = 300;
const USER_Q_MAX = 50;
const USER_LIMIT = 20;
const LABEL_MAX = 100;
const USER_KINDS = ['mm_user', 'email'];
const KIND_OPTIONS = [['mm_user', '메신저 DM'], ['email', '메일'], ['mm_channel', '채널']];
const ADD_FIELDS = ['user_id', 'channel_id', 'kinds', 'label'];
const EDIT_FIELDS = ['label', 'kinds', 'enabled'];
const MSG_LOAD_FAIL = '알림 대상을 불러오지 못했습니다.';
const MSG_SAVE_FAIL = '알림 대상을 저장하지 못했습니다.';
const MSG_DELETE_FAIL = '지우지 못했습니다.';
const MSG_TEST_FAIL = '시험 발송을 하지 못했습니다.';
const MSG_BAD_VALUE = '값을 확인하세요.';
const MSG_GONE = '이미 지워진 대상입니다';
const MSG_DUPLICATE = '이미 등록된 대상입니다';
const MSG_NO_KINDS = '사건 종류를 하나 이상 고르세요';
const MSG_NO_USERS = 'CAPA 메뉴 권한이 있는 사람만 고를 수 있습니다';
const MSG_TOO_MANY = '20명까지 보입니다 — 더 자세히 입력하세요';
const MSG_SEARCH_FAIL = '사람을 찾지 못했습니다.';
const MSG_NO_BOT = '메신저 봇이 연결되어 있지 않아 채널을 고를 수 없습니다';
const MSG_CHANNELS_FAIL = '채널 목록을 불러오지 못했습니다.';
const MSG_CONFIRM_TEST = '이 채널에 시험 글이 올라갑니다. 계속할까요?';
const MSG_CONFIRM_DELETE = '알림 대상을 지웁니다. 보낸 기록은 남습니다. 계속할까요?';

// 탭(CapaAdminTab)과 같은 값 — 탭에서 export 하지 않고 여기에 둔다.
const section = { ...card, padding: '12px 14px', marginBottom: '12px', overflowWrap: 'anywhere' };
const sectionHead = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px', marginBottom: '8px' };
const sectionTitle = { margin: 0, fontSize: '15px' };
const subTitle = { margin: '14px 0 8px', fontSize: '13px' };
const alertLine = { color: COLORS.dangerText, fontSize: '12px', marginBottom: '6px' };
const fieldMsgStyle = { ...alertLine, margin: '4px 0 0' };
const okLine = { color: COLORS.successText, fontSize: '12px' };
const mutedLine = { color: COLORS.textSub, fontSize: '12px' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '10px 12px' };
const labelStyle = { display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '4px' };
const fieldInput = { ...input, padding: '7px 10px', fontSize: '13px' };
const th = { padding: '8px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '7px 8px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const disabledLook = (off) => (off ? { opacity: 0.55, cursor: 'not-allowed' } : {});
const fieldsetStyle = { border: 0, padding: 0, margin: '10px 0 0', minWidth: 0 };
const legendStyle = { ...labelStyle, padding: 0 };
const checkRow = { display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: '13px' };
const checkItem = { display: 'inline-flex', alignItems: 'center', gap: '4px' };
const optList = { marginTop: '6px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', maxHeight: '220px', overflowY: 'auto' };
const optBtn = {
  display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', fontSize: '13px', background: COLORS.surface,
  border: 0, borderBottom: `1px solid ${COLORS.borderSoft}`, cursor: 'pointer', overflowWrap: 'anywhere',
};
const editBox = { background: COLORS.bg, padding: '8px 10px', borderRadius: '8px' };

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const statusOf = (err) => err?.response?.status;
const detailOf = (err) => err?.response?.data?.detail;
const withoutKey = (obj, key) => {
  const next = { ...obj };
  delete next[key];
  return next;
};

export default function CapaNotifyCard({ isAdmin, lock, unlock, busy, onChanged }) {
  const [targets, setTargets] = useState(null);
  const [loadMsg, setLoadMsg] = useState('');
  const [msg, setMsg] = useState('');
  const [testMsgs, setTestMsgs] = useState({});
  const [editing, setEditing] = useState(null);
  const [addKind, setAddKind] = useState('mm_user');
  const [userQ, setUserQ] = useState('');
  const [userItems, setUserItems] = useState(null);
  const [userMsg, setUserMsg] = useState('');
  const [picked, setPicked] = useState(null);
  const [channels, setChannels] = useState(null);
  const [channelMsg, setChannelMsg] = useState('');
  const [channelId, setChannelId] = useState('');
  const [addKinds, setAddKinds] = useState(NOTIFY_KINDS);
  const [addLabel, setAddLabel] = useState('');
  const [addMsg, setAddMsg] = useState('');
  const [addFieldMsg, setAddFieldMsg] = useState({});
  const mountedRef = useRef(true);
  const searchTimerRef = useRef(null);
  const searchSeqRef = useRef(0);
  const channelSeqRef = useRef(0);
  const channelsAskedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const r = await capaAPI.notifyTargets();
        if (!mountedRef.current) return;
        if (Array.isArray(r?.data)) {
          setTargets(r.data.filter(isObj));
        } else {
          setTargets([]);
          setLoadMsg(MSG_LOAD_FAIL);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setTargets([]);
        setLoadMsg(errorText(err, MSG_LOAD_FAIL));
      }
    };
    load();
    return () => {
      mountedRef.current = false;
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
      searchSeqRef.current += 1;
      channelSeqRef.current += 1;
    };
  }, []);

  const userKind = USER_KINDS.includes(addKind);
  // 첫 로드 중(targets === null)에도 쓰기를 막는다 — 늦은 첫 GET 이 방금 추가한 행을 덮지 않게.
  const writeOff = !isAdmin || !!busy || targets === null;
  const canAdd = !writeOff && addKinds.length > 0 && (userKind ? !!picked : !!channelId);

  // 검색 타이머·늦은 응답을 무효로 하고 결과를 비운다.
  const stopSearch = () => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = null;
    searchSeqRef.current += 1;
    setUserItems(null);
    setUserMsg('');
  };

  const dropTarget = (id) => {
    setTargets((cur) => (cur || []).filter((t) => t.id !== id));
    setEditing((cur) => (cur && cur.id === id ? null : cur));
    setTestMsgs((cur) => withoutKey(cur, id));
  };

  // ── 추가 폼 ──
  const onUserQ = (value) => {
    if (!isAdmin) return;
    setUserQ(value);
    const q = value.trim();
    if (q.length < 1 || q.length > USER_Q_MAX) {
      stopSearch();
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchSeqRef.current += 1;
    const seq = searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      searchTimerRef.current = null;
      try {
        const r = await capaAPI.notifyUsers(q);
        if (!mountedRef.current || seq !== searchSeqRef.current) return;
        setUserItems(Array.isArray(r?.data?.items) ? r.data.items.filter(isObj) : []);
        setUserMsg('');
      } catch (err) {
        if (!mountedRef.current || seq !== searchSeqRef.current) return;
        setUserItems(null);
        setUserMsg(errorText(err, MSG_SEARCH_FAIL));
      }
    }, SEARCH_DELAY_MS);
  };

  const onPick = (user) => {
    if (!isAdmin) return;
    stopSearch();
    setUserQ('');
    setPicked(user);
    setAddFieldMsg({});
  };

  const onUnpick = () => {
    stopSearch();
    setUserQ('');
    setPicked(null);
    setAddFieldMsg({});
  };

  const loadChannels = async () => {
    if (!isAdmin) return;
    channelsAskedRef.current = true;
    channelSeqRef.current += 1;
    const seq = channelSeqRef.current;
    setChannelMsg('');
    try {
      const r = await capaAPI.notifyChannels();
      if (!mountedRef.current || seq !== channelSeqRef.current) return;
      const data = isObj(r?.data) ? r.data : {};
      if (data.enabled === false) {
        setChannels({ enabled: false, items: [] });
        setChannelId('');
        setChannelMsg(MSG_NO_BOT);
        return;
      }
      const items = Array.isArray(data.items) ? data.items.filter(isObj) : [];
      setChannels({ enabled: true, items });
      setChannelId((cur) => (items.some((c) => c.id === cur) ? cur : ''));
    } catch (err) {
      if (!mountedRef.current || seq !== channelSeqRef.current) return;
      setChannels({ enabled: false, items: [] });
      setChannelId('');
      setChannelMsg(errorText(err, MSG_CHANNELS_FAIL));
    }
  };

  // 종류를 바꾸면 고른 사람·검색어·결과·고른 채널을 비운다(사건 종류·이름표는 남긴다).
  const onKind = (value) => {
    if (!isAdmin) return;
    stopSearch();
    setUserQ('');
    setPicked(null);
    setChannelId('');
    setAddMsg('');
    setAddFieldMsg({});
    setAddKind(value);
    if (value === 'mm_channel' && !channelsAskedRef.current) loadChannels();
  };

  const onAddKind = (kind, checked) => {
    if (!isAdmin) return;
    setAddKinds((cur) => NOTIFY_KINDS.filter((k) => (k === kind ? checked : cur.includes(k))));
  };

  const addError = (err) => {
    const status = statusOf(err);
    const d = detailOf(err);
    const shown = (field) => {
      if (field === 'user_id') return userKind;
      if (field === 'channel_id') return !userKind;
      return ADD_FIELDS.includes(field);
    };
    if (status === 422) {
      if (isObj(d) && shown(d.field)) setAddFieldMsg({ [d.field]: detailText(d, MSG_BAD_VALUE) });
      else setAddMsg(detailText(d, MSG_SAVE_FAIL));
      return;
    }
    if (status === 409) {
      setAddMsg(MSG_DUPLICATE);
      return;
    }
    if (status === 502) {
      setAddMsg(detailText(d, MSG_SAVE_FAIL));
      return;
    }
    setMsg(detailText(d, MSG_SAVE_FAIL));
  };

  const onAdd = async () => {
    if (!isAdmin) return;
    if (!lock('notify-add')) return;
    try {
      setMsg('');
      setAddMsg('');
      setAddFieldMsg({});
      const kinds = NOTIFY_KINDS.filter((k) => addKinds.includes(k));
      if (kinds.length === 0 || (userKind ? !picked : !channelId)) return;
      if (!mountedRef.current) return;
      const label = addLabel.trim();
      const body = {
        kind: addKind,
        kinds,
        ...(label ? { label } : {}),
        ...(userKind ? { user_id: picked.id } : { channel_id: channelId }),
      };
      const r = await capaAPI.addNotifyTarget(body);
      if (!mountedRef.current) return;
      const row = isObj(r?.data) ? r.data : null;
      // 서버 목록이 최신순이라 맨 앞에 더한다.
      if (row) setTargets((cur) => [row, ...(cur || []).filter((t) => t.id !== row.id)]);
      stopSearch();
      setUserQ('');
      setPicked(null);
      setChannelId('');
      setAddLabel('');
      setAddKinds(NOTIFY_KINDS);
      await onChanged?.();
    } catch (err) {
      if (mountedRef.current) addError(err);
    } finally {
      unlock();
    }
  };

  // ── 수정 — 한 번에 한 행만 ──
  const onStartEdit = (t) => {
    if (!isAdmin) return;
    const kinds = Array.isArray(t.kinds) ? t.kinds : [];
    setEditing({
      id: t.id,
      label: t.label || '',
      kinds: NOTIFY_KINDS.filter((k) => kinds.includes(k)),
      enabled: !!t.enabled,
      msg: '',
      fieldMsg: {},
    });
  };

  const onEditField = (field, value) => setEditing((cur) => (cur ? { ...cur, [field]: value } : cur));

  const onEditKind = (kind, checked) => setEditing((cur) => (cur
    ? { ...cur, kinds: NOTIFY_KINDS.filter((k) => (k === kind ? checked : cur.kinds.includes(k))) }
    : cur));

  const editError = (id, err) => {
    const status = statusOf(err);
    const d = detailOf(err);
    if (status === 404) {
      dropTarget(id);
      setMsg(MSG_GONE);
      return;
    }
    if (status === 422) {
      const text = detailText(d, MSG_BAD_VALUE);
      const field = isObj(d) && EDIT_FIELDS.includes(d.field) ? d.field : null;
      setEditing((cur) => (cur && cur.id === id
        ? { ...cur, msg: field ? '' : text, fieldMsg: field ? { [field]: text } : {} }
        : cur));
      return;
    }
    setMsg(detailText(d, MSG_SAVE_FAIL));
  };

  // 바뀐 칸만 보낸다 — kinds 는 양쪽을 NOTIFY_KINDS 순서로 맞춰 비교한다(순서만 다른 저장 행에서 kinds 가 같이 나가지 않게).
  const onSaveEdit = async () => {
    const ed = editing;
    if (!isAdmin) return;
    if (!lock('notify-edit')) return;
    try {
      if (!ed) return;
      setMsg('');
      const t = (targets || []).find((x) => x.id === ed.id);
      if (!t) {
        setEditing(null);
        return;
      }
      const kinds = NOTIFY_KINDS.filter((k) => ed.kinds.includes(k));
      if (kinds.length === 0) {
        setEditing({ ...ed, msg: MSG_NO_KINDS, fieldMsg: {} });
        return;
      }
      const savedKinds = Array.isArray(t.kinds) ? t.kinds : [];
      const changes = {};
      if (!sameValue(kinds, NOTIFY_KINDS.filter((k) => savedKinds.includes(k)))) changes.kinds = kinds;
      const label = ed.label.trim();
      if (label !== (t.label || '')) changes.label = label || null;
      if (ed.enabled !== !!t.enabled) changes.enabled = ed.enabled;
      if (Object.keys(changes).length === 0) {
        setEditing(null);
        return;
      }
      setEditing({ ...ed, msg: '', fieldMsg: {} });
      if (!mountedRef.current) return;
      const r = await capaAPI.patchNotifyTarget(ed.id, changes);
      if (!mountedRef.current) return;
      const row = isObj(r?.data) ? r.data : null;
      if (row) setTargets((cur) => (cur || []).map((x) => (x.id === ed.id ? row : x)));
      setEditing(null);
      await onChanged?.();
    } catch (err) {
      if (mountedRef.current && ed) editError(ed.id, err);
    } finally {
      unlock();
    }
  };

  // ── 삭제 — 성공·404 모두 목록에서 뺀다 ──
  const onDelete = async (t) => {
    if (!isAdmin) return;
    if (!lock('notify-delete')) return;
    try {
      setMsg('');
      const ok = await confirmDialog(MSG_CONFIRM_DELETE);
      if (!ok || !mountedRef.current) return;
      try {
        await capaAPI.deleteNotifyTarget(t.id);
      } catch (err) {
        if (statusOf(err) !== 404) throw err;
      }
      if (!mountedRef.current) return;
      dropTarget(t.id);
      await onChanged?.();
    } catch (err) {
      if (mountedRef.current) setMsg(errorText(err, MSG_DELETE_FAIL));
    } finally {
      unlock();
    }
  };

  // ── 시험 발송 — 채널만 확인 · 200(성공·실패)·404 뒤에 onChanged(감사 행이 생긴다) · 429 는 그 행 아래 문구 ──
  const onTest = async (t) => {
    if (!isAdmin) return;
    if (!lock('notify-test')) return;
    try {
      setMsg('');
      setTestMsgs((cur) => withoutKey(cur, t.id));
      if (t.kind === 'mm_channel') {
        const ok = await confirmDialog(MSG_CONFIRM_TEST);
        if (!ok) return;
      }
      if (!mountedRef.current) return;
      let r;
      try {
        r = await capaAPI.testNotifyTarget(t.id);
      } catch (err) {
        if (!mountedRef.current) return;
        if (statusOf(err) === 429) {
          setTestMsgs((cur) => ({ ...cur, [t.id]: { text: detailText(detailOf(err), MSG_TEST_FAIL), ok: false } }));
          return;
        }
        if (statusOf(err) === 404) {
          dropTarget(t.id);
          setMsg(MSG_GONE);
          await onChanged?.();
          return;
        }
        throw err;
      }
      if (!mountedRef.current) return;
      setTestMsgs((cur) => ({ ...cur, [t.id]: { text: testResultText(r?.data), ok: r?.data?.ok === true } }));
      await onChanged?.();
    } catch (err) {
      if (mountedRef.current) setMsg(errorText(err, MSG_TEST_FAIL));
    } finally {
      unlock();
    }
  };

  // ── 그리기 ──
  const renderEdit = (t) => {
    const base = `capa-notify-edit-${t.id}`;
    return (
      <div data-testid={base} style={editBox}>
        <div style={grid}>
          <div style={{ minWidth: 0 }}>
            <label htmlFor={`${base}-label`} style={labelStyle}>이름표</label>
            <input id={`${base}-label`} type="text" maxLength={LABEL_MAX} value={editing.label} disabled={writeOff}
              onChange={(ev) => onEditField('label', ev.target.value)}
              style={{ ...fieldInput, ...disabledLook(writeOff) }} />
            {editing.fieldMsg.label && (
              <div data-testid="capa-notify-edit-msg-label" style={fieldMsgStyle}>{editing.fieldMsg.label}</div>
            )}
          </div>
          <div style={{ minWidth: 0, fontSize: '13px', paddingTop: '18px' }}>
            <span style={checkItem}>
              <input id={`${base}-enabled`} type="checkbox" checked={editing.enabled} disabled={writeOff}
                onChange={(ev) => onEditField('enabled', ev.target.checked)} />
              <label htmlFor={`${base}-enabled`}>켜짐</label>
            </span>
            {editing.fieldMsg.enabled && (
              <div data-testid="capa-notify-edit-msg-enabled" style={fieldMsgStyle}>{editing.fieldMsg.enabled}</div>
            )}
          </div>
        </div>
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>사건 종류</legend>
          <div style={checkRow}>
            {NOTIFY_KINDS.map((k) => {
              const id = `${base}-kinds-${k}`;
              return (
                <span key={k} style={checkItem}>
                  <input id={id} type="checkbox" checked={editing.kinds.includes(k)} disabled={writeOff}
                    onChange={(ev) => onEditKind(k, ev.target.checked)} />
                  <label htmlFor={id}>{NOTIFY_KIND_LABELS[k]}</label>
                </span>
              );
            })}
          </div>
          {editing.fieldMsg.kinds && (
            <div data-testid="capa-notify-edit-msg-kinds" style={fieldMsgStyle}>{editing.fieldMsg.kinds}</div>
          )}
        </fieldset>
        {editing.msg && <div role="alert" data-testid="capa-notify-edit-msg" style={fieldMsgStyle}>{editing.msg}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
          <button type="button" onClick={onSaveEdit} disabled={writeOff}
            style={{ ...btn('primary', 'sm'), ...disabledLook(writeOff) }}>
            저장
          </button>
          <button type="button" onClick={() => setEditing(null)} disabled={busy === 'notify-edit'}
            style={{ ...btn('secondary', 'sm'), ...disabledLook(busy === 'notify-edit') }}>
            취소
          </button>
        </div>
      </div>
    );
  };

  const renderRow = (t) => {
    const text = targetText(t);
    const test = testMsgs[t.id];
    return (
      <Fragment key={t.id}>
        <tr data-testid={`capa-notify-row-${t.id}`}>
          <td style={{ ...td, minWidth: '180px' }}>
            <div>{text.who}</div>
            {text.sub && <div style={mutedLine}>{text.sub}</div>}
          </td>
          <td style={td}>{kindsText(t.kinds)}</td>
          <td style={{ ...td, whiteSpace: 'nowrap' }}>
            <span style={badge(t.enabled ? 'success' : 'default')}>{t.enabled ? '켜짐' : '꺼짐'}</span>
          </td>
          <td style={td}>{lastSendText(t.last)}</td>
          <td style={td}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              <button type="button" onClick={() => onTest(t)} disabled={writeOff}
                style={{ ...btn('secondary', 'sm'), ...disabledLook(writeOff) }}>
                시험 발송
              </button>
              <button type="button" onClick={() => onStartEdit(t)} disabled={writeOff}
                style={{ ...btn('secondary', 'sm'), ...disabledLook(writeOff) }}>
                수정
              </button>
              <button type="button" onClick={() => onDelete(t)} disabled={writeOff}
                style={{ ...btn('danger', 'sm'), ...disabledLook(writeOff) }}>
                삭제
              </button>
            </div>
          </td>
        </tr>
        {test && (
          <tr>
            <td colSpan={5} style={td}>
              <div data-testid={`capa-notify-test-${t.id}`} style={test.ok ? okLine : { ...alertLine, marginBottom: 0 }}>{test.text}</div>
            </td>
          </tr>
        )}
        {editing && editing.id === t.id && (
          <tr>
            <td colSpan={5} style={td}>{renderEdit(t)}</td>
          </tr>
        )}
      </Fragment>
    );
  };

  const renderUserPicker = () => (
    <div style={{ minWidth: 0 }}>
      {picked ? (
        <>
          <div style={labelStyle}>받는 사람</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            <span data-testid="capa-notify-user-picked">{`${picked.name || DASH} · ${picked.email_hint || DASH}`}</span>
            <button type="button" onClick={onUnpick} disabled={writeOff}
              style={{ ...btn('secondary', 'sm'), ...disabledLook(writeOff) }}>
              바꾸기
            </button>
          </div>
        </>
      ) : (
        <>
          <label htmlFor="capa-notify-user-q" style={labelStyle}>받는 사람 찾기(이름·메일)</label>
          <input id="capa-notify-user-q" data-testid="capa-notify-user-q" type="text" autoComplete="off" value={userQ}
            disabled={writeOff} onChange={(ev) => onUserQ(ev.target.value)}
            style={{ ...fieldInput, ...disabledLook(writeOff) }} />
          {userMsg && <div style={fieldMsgStyle}>{userMsg}</div>}
          {userItems && userItems.length === 0 && <div style={{ ...mutedLine, marginTop: '4px' }}>{MSG_NO_USERS}</div>}
          {userItems && userItems.length > 0 && (
            <div style={optList}>
              {userItems.map((u) => (
                <button key={u.id} type="button" data-testid={`capa-notify-user-opt-${u.id}`} onClick={() => onPick(u)}
                  disabled={writeOff} style={{ ...optBtn, ...disabledLook(writeOff) }}>
                  {`${u.name || DASH} · ${u.email_hint || DASH}`}
                </button>
              ))}
            </div>
          )}
          {userItems && userItems.length >= USER_LIMIT && <div style={{ ...mutedLine, marginTop: '4px' }}>{MSG_TOO_MANY}</div>}
        </>
      )}
      {addFieldMsg.user_id && <div data-testid="capa-notify-add-msg-user_id" style={fieldMsgStyle}>{addFieldMsg.user_id}</div>}
    </div>
  );

  const renderChannelPicker = () => (
    <div style={{ minWidth: 0 }}>
      {channels?.enabled ? (
        <>
          <label htmlFor="capa-notify-channel" style={labelStyle}>채널</label>
          <select id="capa-notify-channel" data-testid="capa-notify-channel" value={channelId} disabled={writeOff}
            onChange={(ev) => setChannelId(ev.target.value)} style={{ ...fieldInput, ...disabledLook(writeOff) }}>
            <option value="">고르세요</option>
            {channels.items.map((c) => (
              <option key={c.id} value={c.id}>{`${c.label || c.name || c.id}${c.type === 'P' ? ' (비공개)' : ''}`}</option>
            ))}
          </select>
        </>
      ) : (
        <div style={labelStyle}>채널</div>
      )}
      {channels === null && !channelMsg && <div style={mutedLine}>불러오는 중…</div>}
      {channelMsg && <div data-testid="capa-notify-channel-msg" style={fieldMsgStyle}>{channelMsg}</div>}
      {addFieldMsg.channel_id && (
        <div data-testid="capa-notify-add-msg-channel_id" style={fieldMsgStyle}>{addFieldMsg.channel_id}</div>
      )}
      <button type="button" onClick={() => loadChannels()} disabled={writeOff}
        style={{ ...btn('secondary', 'sm'), marginTop: '6px', ...disabledLook(writeOff) }}>
        ↻ 채널 목록
      </button>
    </div>
  );

  const rows = targets || [];
  return (
    <section aria-labelledby="capa-admin-notify-title" data-testid="capa-admin-notify" style={section}>
      <div style={sectionHead}>
        <h3 id="capa-admin-notify-title" style={sectionTitle}>알림 대상</h3>
      </div>
      {(loadMsg || msg) && (
        <div role="alert" data-testid="capa-admin-notify-msg" style={alertLine}>
          {loadMsg && <div>{loadMsg}</div>}
          {msg && <div>{msg}</div>}
        </div>
      )}
      {targets === null && <div role="status" style={mutedLine}>불러오는 중…</div>}
      {targets !== null && (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="알림 대상" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
            <thead><tr>{['대상', '사건 종류', '켜짐', '마지막 발송', '동작'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(renderRow)}
              {rows.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }}>등록된 알림 대상이 없습니다</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <h4 style={subTitle}>대상 추가</h4>
      <div data-testid="capa-notify-add">
        <div style={grid}>
          <div style={{ minWidth: 0 }}>
            <label htmlFor="capa-notify-kind" style={labelStyle}>종류</label>
            <select id="capa-notify-kind" data-testid="capa-notify-kind" value={addKind} disabled={writeOff}
              onChange={(ev) => onKind(ev.target.value)} style={{ ...fieldInput, ...disabledLook(writeOff) }}>
              {KIND_OPTIONS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </select>
          </div>
          {userKind ? renderUserPicker() : renderChannelPicker()}
          <div style={{ minWidth: 0 }}>
            <label htmlFor="capa-notify-label" style={labelStyle}>이름표(선택)</label>
            <input id="capa-notify-label" data-testid="capa-notify-label" type="text" maxLength={LABEL_MAX} value={addLabel}
              disabled={writeOff} onChange={(ev) => setAddLabel(ev.target.value)}
              style={{ ...fieldInput, ...disabledLook(writeOff) }} />
            {addFieldMsg.label && <div data-testid="capa-notify-add-msg-label" style={fieldMsgStyle}>{addFieldMsg.label}</div>}
          </div>
        </div>
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>사건 종류</legend>
          <div style={checkRow}>
            {NOTIFY_KINDS.map((k) => {
              const id = `capa-notify-kinds-${k}`;
              return (
                <span key={k} style={checkItem}>
                  <input id={id} data-testid={id} type="checkbox" checked={addKinds.includes(k)} disabled={writeOff}
                    onChange={(ev) => onAddKind(k, ev.target.checked)} />
                  <label htmlFor={id}>{NOTIFY_KIND_LABELS[k]}</label>
                </span>
              );
            })}
          </div>
          {addFieldMsg.kinds && <div data-testid="capa-notify-add-msg-kinds" style={fieldMsgStyle}>{addFieldMsg.kinds}</div>}
        </fieldset>
        {addMsg && <div role="alert" data-testid="capa-notify-add-msg" style={{ ...alertLine, marginTop: '8px' }}>{addMsg}</div>}
        <button type="button" onClick={onAdd} disabled={!canAdd}
          style={{ ...btn('primary', 'sm'), marginTop: '10px', ...disabledLook(!canAdd) }}>
          추가
        </button>
      </div>
    </section>
  );
}
